import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useEditMode } from '../contexts/EditModeContext';
import type { CmsBlock } from '../types/cms';

interface Props {
  block: CmsBlock;
  children: React.ReactNode;
}

/**
 * Wraps a CMS block in edit mode with:
 * - Hover and selection highlight borders
 * - Block type label tooltip
 * - Drag handle for reordering (dnd-kit sortable)
 * - Click handler that dispatches BLOCK_SELECT to the parent builder
 */
export function EditableBlockWrapper({ block, children }: Props) {
  const { selectedBlockId, dispatch } = useEditMode();
  const [isHovered, setIsHovered] = useState(false);
  const isSelected = selectedBlockId === block.id;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    position: 'relative',
  };

  function handleClick(e: React.MouseEvent) {
    e.stopPropagation();
    dispatch({ type: 'SELECT_BLOCK', blockId: block.id });
  }

  const showOverlay = isHovered || isSelected;

  return (
    <div
      ref={setNodeRef}
      style={style}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      {showOverlay && (
        <>
          {/* Selection/hover border */}
          <div
            className="pointer-events-none absolute inset-0 z-20"
            style={{
              outline: isSelected ? '2px solid #6366f1' : '2px dashed #a5b4fc',
              outlineOffset: '-2px',
            }}
          />
          {/* Block type label */}
          <div
            className="absolute z-30 flex items-center gap-1.5"
            style={{ top: -22, left: 0 }}
          >
            <span
              className="rounded-t px-2 py-0.5 text-[10px] font-semibold leading-tight text-white"
              style={{ background: isSelected ? '#6366f1' : '#818cf8' }}
            >
              {block.type.replace(/_/g, ' ')}
            </span>
          </div>
          {/* Move up/down + delete buttons */}
          <div
            className="absolute z-30 flex flex-col items-center gap-0.5"
            style={{ right: -30, top: '50%', transform: 'translateY(-50%)' }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.parent.postMessage({ type: 'BLOCK_MOVE', blockId: block.id, direction: 'up' }, '*');
              }}
              className="flex items-center justify-center rounded-t hover:brightness-110"
              style={{
                background: isSelected ? '#6366f1' : '#818cf8',
                width: 26,
                height: 22,
                color: 'white',
                fontSize: 11,
              }}
              title="Move up"
            >
              ▲
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.parent.postMessage({ type: 'BLOCK_MOVE', blockId: block.id, direction: 'down' }, '*');
              }}
              className="flex items-center justify-center hover:brightness-110"
              style={{
                background: isSelected ? '#6366f1' : '#818cf8',
                width: 26,
                height: 22,
                color: 'white',
                fontSize: 11,
              }}
              title="Move down"
            >
              ▼
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                window.parent.postMessage({ type: 'BLOCK_DELETE', blockId: block.id }, '*');
              }}
              className="flex items-center justify-center rounded-b hover:bg-red-600"
              style={{
                background: '#ef4444',
                width: 26,
                height: 22,
                color: 'white',
                fontSize: 11,
              }}
              title="Delete block"
            >
              ✕
            </button>
          </div>
        </>
      )}
      {children}
    </div>
  );
}
